import { createFileRoute } from '@tanstack/react-router';

import { Text, type TextProps } from '@/components/ui/text';
import {
  Group,
  Section,
  Swatch,
  Tiles,
} from '@/features/design-system/showcase-primitives';
import { PLACEMENT_CATALOG } from '@/features/editor/placement-catalog';
import { ROOM_PALETTE } from '@/features/editor/room-palette';

export const Route = createFileRoute('/design-system/tokens')({
  component: TokensPage,
});

const SURFACE_TOKENS = [
  { name: 'background', token: '--background' },
  { name: 'foreground', token: '--foreground' },
  { name: 'card', token: '--card' },
  { name: 'card-foreground', token: '--card-foreground' },
  { name: 'popover', token: '--popover' },
  { name: 'popover-foreground', token: '--popover-foreground' },
  { name: 'primary', token: '--primary' },
  { name: 'primary-foreground', token: '--primary-foreground' },
  { name: 'secondary', token: '--secondary' },
  { name: 'secondary-foreground', token: '--secondary-foreground' },
  { name: 'muted', token: '--muted' },
  { name: 'muted-foreground', token: '--muted-foreground' },
  { name: 'accent', token: '--accent' },
  { name: 'accent-foreground', token: '--accent-foreground' },
  { name: 'border', token: '--border' },
  { name: 'input', token: '--input' },
  { name: 'ring', token: '--ring' },
  { name: 'destructive', token: '--destructive' },
] as const;

const TONE_TOKENS = [
  { name: 'tone-success', token: '--tone-success', hex: '#059669' },
  { name: 'tone-warning', token: '--tone-warning', hex: '#d97706' },
  { name: 'tone-info', token: '--tone-info', hex: '#2563eb' },
] as const;

const CANVAS_TOKENS = [
  { name: 'selection', token: '--selection', hex: '#2563eb' },
  {
    name: 'selection-halo',
    token: '--selection-halo',
    hex: 'oklch(from var(--selection) l c h / 0.18)',
  },
  { name: 'pulse-orphan', token: '--pulse-orphan', hex: '#ef4444' },
  { name: 'node-stroke', token: '--node-stroke', hex: '#0f172a' },
  { name: 'node-glyph', token: '--node-glyph', hex: '#0f172a' },
  { name: 'ring-default', token: '--ring-default', hex: '#cbd5e1' },
] as const;

const ALIAS_TOKENS = [
  {
    name: 'destructive-soft',
    token: '--destructive-soft',
    hex: 'mix(destructive, transparent, 10%)',
  },
  {
    name: 'destructive-tint',
    token: '--destructive-tint',
    hex: 'mix(destructive, transparent, 20%)',
  },
  {
    name: 'ring-invalid',
    token: '--ring-invalid',
    hex: 'mix(destructive, transparent, 20%)',
  },
  {
    name: 'focus-ring',
    token: '--focus-ring',
    hex: 'mix(ring, transparent, 50%)',
  },
] as const;

const RADII = [
  { name: 'radius-sm', cls: 'rounded-sm', px: 6 },
  { name: 'radius-md', cls: 'rounded-md', px: 8 },
  { name: 'radius-lg', cls: 'rounded-lg', px: 10 },
  { name: 'radius-xl', cls: 'rounded-xl', px: 14 },
  { name: 'radius-2xl', cls: 'rounded-2xl', px: 18 },
  { name: 'radius-3xl', cls: 'rounded-3xl', px: 22 },
  { name: 'radius-4xl', cls: 'rounded-4xl', px: 26 },
] as const;

const SPACING = [
  { name: '0.5', px: 2 },
  { name: '1', px: 4 },
  { name: '1.5', px: 6 },
  { name: '2', px: 8 },
  { name: '2.5', px: 10 },
  { name: '3', px: 12 },
  { name: '4', px: 16 },
  { name: '6', px: 24 },
  { name: '8', px: 32 },
] as const;

const VARIANTS: ReadonlyArray<{
  variant: NonNullable<TextProps['variant']>;
  example: string;
  spec: string;
}> = [
  { variant: 'display', example: 'Plan your home', spec: '24/600/-0.02em' },
  { variant: 'title', example: 'Rooms sidebar', spec: '18/600/-0.015em' },
  { variant: 'headline', example: 'Lights Planner', spec: '16/600/-0.01em' },
  { variant: 'body', example: 'No plans yet', spec: '14/400' },
  { variant: 'label', example: 'Room', spec: '12/500' },
  { variant: 'caption', example: 'Auto-saved 2s ago', spec: '12/400 muted' },
  { variant: 'eyebrow', example: 'INTERNAL', spec: '12/500 upper +tracking' },
  {
    variant: 'code',
    example: 'placement-catalog.ts',
    spec: '12/400 mono',
  },
];

function TokensPage() {
  return (
    <div className="flex flex-col gap-14">
      <header className="flex flex-col gap-2">
        <Text variant="eyebrow">Tokens</Text>
        <Text variant="display" as="h1">
          The values everything is built from
        </Text>
        <Text variant="body" tone="muted" className="max-w-2xl">
          Surfaces come from shadcn <code className="font-mono">base-nova</code>;
          domain accents and canvas tokens are mirrored from the editor source.
          Every value is a CSS variable — flipping{' '}
          <code className="font-mono">.dark</code> on{' '}
          <code className="font-mono">html</code> updates the whole page.
        </Text>
      </header>

      <Section
        eyebrow="Colors"
        title="Surfaces (light)"
        description="Neutral monochrome ramp. The shell never carries hue — color enters only through domain accents and canvas state."
      >
        <Tiles cols={4}>
          {SURFACE_TOKENS.map((t) => (
            <Swatch
              key={t.name}
              label={t.name}
              value={`var(${t.token})`}
              hint={t.token}
            />
          ))}
        </Tiles>
      </Section>

      <Section
        eyebrow="Colors"
        title="Domain category"
        description="Light = amber, Socket = blue, Network = green. These read on the placement toolbar swatches and as the inner fill of every Placed Item dot."
      >
        <Tiles cols={3}>
          {PLACEMENT_CATALOG.map((c) => (
            <div key={c.category} className="flex flex-col gap-3">
              <Swatch
                label={c.label}
                value={c.color}
                hint={`--category-${c.category} · ${c.color}`}
              />
              <ul className="flex flex-col gap-1">
                {c.subtypes.map((s) => (
                  <li
                    key={s.value}
                    className="flex items-center justify-between gap-2"
                  >
                    <Text variant="caption">{s.label}</Text>
                    <Text variant="code" tone="muted">
                      {s.value}
                    </Text>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </Tiles>
      </Section>

      <Section
        eyebrow="Colors"
        title="Room palette"
        description="Sixteen hand-picked colors. Hand-picked so several rooms can share a canvas without collision; no free-form HSL picker."
      >
        <Tiles cols={8}>
          {ROOM_PALETTE.map((color, idx) => (
            <Swatch
              key={color}
              label={`#${idx + 1}`}
              value={color}
              hint={color}
            />
          ))}
        </Tiles>
      </Section>

      <Section
        eyebrow="Colors"
        title="Canvas accents"
        description="What the Konva layer pulls in. Hex literals match the catalog; the CSS variables are mirrors that React chrome can use."
      >
        <Tiles cols={3}>
          {CANVAS_TOKENS.map((t) => (
            <Swatch
              key={t.name}
              label={t.name}
              value={`var(${t.token})`}
              hint={`${t.token} · ${t.hex}`}
            />
          ))}
        </Tiles>
      </Section>

      <Section
        eyebrow="Colors"
        title="Semantic tones"
        description="Mirrored on the Text primitive's `tone` axis. Use sparingly — the system stays mostly neutral."
      >
        <Tiles cols={3}>
          {TONE_TOKENS.map((t) => (
            <Swatch
              key={t.name}
              label={t.name}
              value={`var(${t.token})`}
              hint={`${t.token} · ${t.hex}`}
            />
          ))}
        </Tiles>
      </Section>

      <Section
        eyebrow="Colors"
        title="Aliases"
        description="Derived tokens used for chrome states. Each is a transparent mix on top of an existing surface."
      >
        <Tiles cols={4}>
          {ALIAS_TOKENS.map((t) => (
            <Swatch
              key={t.name}
              label={t.name}
              value={`var(${t.token})`}
              hint={t.hex}
            />
          ))}
        </Tiles>
      </Section>

      <Section
        eyebrow="Shape"
        title="Corner radii"
        description="A single ramp tied to --radius (10px). Buttons and inputs sit at md/lg, cards/dialogs at xl, badges at 4xl, dots at full."
      >
        <Tiles cols={4}>
          {RADII.map((r) => (
            <div key={r.name} className="flex flex-col gap-2">
              <div
                className={`h-16 w-full bg-muted ring-1 ring-foreground/10 ${r.cls}`}
              />
              <div className="flex flex-col gap-0.5">
                <Text variant="label">{r.name}</Text>
                <Text variant="code" tone="muted">
                  {r.px}px
                </Text>
              </div>
            </div>
          ))}
        </Tiles>
      </Section>

      <Section
        eyebrow="Layout"
        title="Spacing scale"
        description="Tailwind v4 defaults, used densely. Common values: gap-1/1.5/2/3/4, padding p-3/p-4."
      >
        <Group label="Examples in pixels">
          <div className="flex flex-wrap items-end gap-4">
            {SPACING.map((s) => (
              <div key={s.name} className="flex flex-col items-center gap-2">
                <div
                  className="bg-foreground/80"
                  style={{ width: s.px, height: s.px }}
                />
                <div className="flex flex-col items-center gap-0.5">
                  <Text variant="label">{s.name}</Text>
                  <Text variant="code" tone="muted">
                    {s.px}px
                  </Text>
                </div>
              </div>
            ))}
          </div>
        </Group>
      </Section>

      <Section
        eyebrow="Type"
        title="Variants"
        description="Eight semantic variants. Use these as the default vocabulary; size/weight/tone only fine-tune."
      >
        <div className="flex flex-col gap-4">
          {VARIANTS.map((v) => (
            <div
              key={v.variant}
              className="flex items-baseline justify-between gap-6 border-b border-border pb-3 last:border-b-0"
            >
              <div className="flex min-w-0 flex-col gap-1">
                <Text variant="caption">variant=&quot;{v.variant}&quot;</Text>
                <Text variant={v.variant}>{v.example}</Text>
              </div>
              <Text variant="code" tone="muted" className="shrink-0">
                {v.spec}
              </Text>
            </div>
          ))}
        </div>
      </Section>

      <Section
        eyebrow="Type"
        title="Tones"
        description="Orthogonal `tone` axis — overlays any variant. Only six in active rotation."
      >
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {(
            [
              'default',
              'muted',
              'subtle',
              'primary',
              'destructive',
              'success',
              'warning',
              'info',
            ] as const
          ).map((tone) => (
            <div
              key={tone}
              className="rounded-md border border-border bg-card px-3 py-2"
            >
              <Text variant="caption">tone=&quot;{tone}&quot;</Text>
              <Text variant="body" tone={tone}>
                Save milestone
              </Text>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
